import { ArrowRightIcon, CheckIcon } from 'lucide-react'
import { useMemo } from 'react'
import { useSessionStorage } from 'usehooks-ts'

import { emitEmployerRenewalsEvent, RENEWAL_EVENTS } from 'apps/employer_renewals/events'
import { REVIEW_ACCEPTED_ROUTE } from 'apps/employer_renewals/routes'
import { Banner, ButtonLink, TextLink } from 'slab'

type Props = {
  employerName: string
  renewalsPath: string
  hasFinalizedPricing: boolean
  hasAcceptedPreliminaryQuote: boolean
  hasAcceptedFinalizedQuote: boolean
}

type BannerState = {
  title: string
  buttonText: string
  acceptedQuote?: boolean
}

const preliminaryState = (employerName: string): BannerState => {
  return {
    title: 'Review and confirm your 2026 allowance strategy by November 4th',
    buttonText: `Review ${employerName}'s 2026 benefits`,
  }
}

const preliminaryAcceptedState: BannerState = {
  title: "You've confirmed your 2026 allowance strategy",
  buttonText: 'View estimates',
  acceptedQuote: true,
}

const finalizedState: BannerState = {
  title: 'Final rates for your 2026 renewal are now available',
  buttonText: 'Review and make any changes by November 4th',
}

const finalizedAcceptedState: BannerState = {
  title: "You've confirmed your 2026 benefits renewal! Eligible employees will be invited to enroll on November 5th.",
  buttonText: 'View renewal',
  acceptedQuote: true,
}

export default function RenewalsDashboardBanner ({
  employerName,
  renewalsPath,
  hasFinalizedPricing,
  hasAcceptedPreliminaryQuote,
  hasAcceptedFinalizedQuote,
}: Props) {
  const bannerState = useMemo(() => {
    if (hasAcceptedFinalizedQuote) {
      return finalizedAcceptedState
    } else if (hasFinalizedPricing) {
      return finalizedState
    } else if (hasAcceptedPreliminaryQuote) {
      return preliminaryAcceptedState
    } else {
      return preliminaryState(employerName)
    }
  }, [hasAcceptedFinalizedQuote, hasAcceptedPreliminaryQuote, hasFinalizedPricing, employerName])

  const [isDismissed, setIsDismissed] = useSessionStorage('er-renewals-banner-dismissed', false)

  const onClick = () => {
    emitEmployerRenewalsEvent(RENEWAL_EVENTS.DASHBOARD_BANNER_CLICKED, !hasFinalizedPricing)
  }

  if (bannerState.acceptedQuote) {
    if (isDismissed) return null

    return (
      <div className='mb-10'>
        <Banner
          title={bannerState.title}
          type='info'
          icon={CheckIcon}
          cta={
            <ButtonLink href={`${renewalsPath}${REVIEW_ACCEPTED_ROUTE}`} onClick={onClick}>
              {bannerState.buttonText}
            </ButtonLink>
          }
          onDismiss={() => setIsDismissed(true)}
        />
      </div>
    )
  }

  return (
    <div className='mb-10 text-white'>
      <Banner
        title={bannerState.title}
        type='emphasis'
        cta={
          <TextLink href={renewalsPath} color='inherit' rightIcon={ArrowRightIcon} onClick={onClick}>
            {bannerState.buttonText}
          </TextLink>
}
      />
    </div>
  )
}
